<template>
  <div class="tab-container">
    <app-cells>
      <app-input
        v-model="search_filter"
        class="input--300"
        name="search"
        placeholder="Search"
        type="search"
        @input.native="onLoanSearch"
      />
    </app-cells>
    <app-spinner v-if="!page_loaded"/>
    <template v-else>
      <v-client-table
        ref="loanTable"
        :class="{ 'table-default--loading': table_preloader }"
        :columns="table.columns"
        :data="table.items"
        :options="table.options"
        class="table-default"
      >
        <template #h__employee>
          <div class="table-default__left">Employee</div>
        </template>
        <template #h__position>
          <div class="table-default__left">Position</div>
        </template>
        <template #details="{ row }">
          <div class="table-default__controls heslb-loans__details-control">
            <span
              class="status-tag status-tag--flex"
              :style="{ cursor: row.deductions_count > 0 ? 'pointer' : 'default', 'backgroundColor': '#77b017' }"
              @click.stop="toggleDetails(row)"
            >
              <span>{{ row.deductions_count }}</span>
              <img
                v-if="row.deductions_count > 0"
                :class="{ 'heslb-loans__details-arrow--open': isDetailsOpen(row.id) }"
                alt=""
                src="@/assets/img/arrow-toggler-bottom-white.svg"
              >
              <img
                v-if="detailsLoadingId === row.id"
                alt=""
                class="heslb-loans__details-preloader"
                src="@/assets/img/preloader.svg"
              >
            </span>
          </div>
        </template>
        <template #employee="{ row }">
          <div class="table-default__left">
            <router-link
              :to=" { name: 'employees-profile', params: { id: row.employee.id } }"
              class="table-default__link-border"
            >
              {{ row.employee.full_name }}
            </router-link>
          </div>
        </template>
        <template #position="{ row }">
          <div class="table-default__left">{{ row.employee.position }}</div>
        </template>
        <template #profile="{ row }">{{ row.profile || '-' }}</template>
        <template #initial_amount="{ row }">{{ formatAmount(row.initial_amount) }} TSh</template>
        <template #deducted_amount="{ row }">{{ formatAmount(row.deducted_amount) }} TSh</template>
        <template #current_balance="{ row }">{{ formatAmount(row.current_balance) }} TSh</template>
        <template #created_at="{ row }">{{ formatDateTime(row.created_at) }}</template>
        <template #actions="{ row }">
          <div class="table-default__controls">
            <app-button theme="info" size="small" @click.native.stop="$emit('edit-loan', row)">Edit</app-button>
          </div>
        </template>
        <template #child_row="{ row }">
          <v-client-table
            :columns="deductionsTable.columns"
            :data="row.deductions"
            :options="deductionsTable.options"
            class="table-default"
          >
            <template #h__payroll_month>
              <div class="table-default__left">Month</div>
            </template>
            <template #payroll_month="{ row: deduction }">
              <router-link
                v-if="deduction.payroll"
                :to="{ name: 'employees-payroll-employees', params: { id: deduction.payroll } }"
                class="table-default__link-border"
              >
                {{ formatPayrollMonth(deduction.payroll_month) }}
              </router-link>
              <span v-else>{{ formatPayrollMonth(deduction.payroll_month) }}</span>
            </template>
            <template #status="{ row: deduction }">
              <span :style="{ backgroundColor: deduction.status.color }" class="status-tag">
                {{ deduction.status.name }}
              </span>
            </template>
            <template #gross_salary_snapshot="{ row: deduction }">
              {{ formatAmount(deduction.gross_salary_snapshot) }} TSh
            </template>
            <template #amount="{ row: deduction }">{{ formatAmount(deduction.amount) }} TSh</template>
            <template #balance_before="{ row: deduction }">{{ formatAmount(deduction.balance_before) }} TSh</template>
            <template #balance_after="{ row: deduction }">{{ formatAmount(deduction.balance_after) }} TSh</template>
          </v-client-table>
        </template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
            v-if="pagination.pages > 1"
            v-model="pagination.page"
            :clickHandler="onClickPagination"
            :container-class="'pagination'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :next-text="'>'"
            :page-class="'pagination__item'"
            :page-count="pagination.pages"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :prev-link-class="'pagination__link pagination__link--prev'"
            :prev-text="'<'"
          />
        </div>
        <app-table-records :all="pagination.count" :count="table.items.length" :page="pagination.page"/>
      </app-cells>
    </template>
  </div>
</template>

<script>
import { getEmployeesHeslbLoans, getEmployeesHeslbLoansDeductions } from '@/http'
import debounce from 'lodash/debounce'
import { format, parseISO } from 'date-fns'

export default {
  name: 'HESLBLoansActive',
  data() {
    return {
      search_filter: '',
      table: {
        items: [],
        columns: [
          'details',
          'employee',
          'position',
          'profile',
          'initial_amount',
          'deducted_amount',
          'current_balance',
          'created_at',
          'actions',
        ],
        options: {
          headings: {
            details: 'Details',
            employee: 'Employee',
            position: 'Position',
            profile: 'Profile / account',
            initial_amount: 'Amount',
            deducted_amount: 'Deducted',
            current_balance: 'Current balance',
            created_at: 'Created at',
            actions: 'Actions',
          },
          showChildRowToggler: false,
          pagination: {show: false},
          sortable: [],
          perPage: 50,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      deductionsTable: {
        columns: [
          'payroll_month',
          'status',
          'gross_salary_snapshot',
          'percent_snapshot',
          'amount',
          'balance_before',
          'balance_after',
        ],
        options: {
          headings: {
            payroll_month: 'Month',
            status: 'Status',
            gross_salary_snapshot: 'Gross salary',
            percent_snapshot: 'Percent',
            amount: 'Amount',
            balance_before: 'Balance before',
            balance_after: 'Balance after',
          },
          filterable: false,
          pagination: {show: false},
          sortable: [],
          perPage: 100,
          rowClassCallback: () => 'table-default__row',
        },
      },
      detailsLoadingId: null,
      openedDeductionIds: [],
      pagination: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_preloader: false,
      page_loaded: false,
    }
  },
  created() {
    this.fetchItems()
  },
  methods: {
    fetchItems() {
      this.table_preloader = true
      getEmployeesHeslbLoans({
        page: this.pagination.page,
        page_size: 50,
        search: this.search_filter,
        status: 'active'
      })
        .then(response => {
          this.table.items = response.data.results
          this.pagination.pages = response.data.pages
          this.pagination.count = response.data.count
          this.openedDeductionIds = []
          this.table_preloader = false
          this.page_loaded = true
        })
    },
    onClickPagination(page) {
      this.pagination.page = page
      this.fetchItems()
    },
    toggleDetails(row) {
      if (row.deductions_count < 1 || this.detailsLoadingId !== null) return

      if (this.isDetailsOpen(row.id)) {
        this.$refs.loanTable.toggleChildRow(row.id)
        this.openedDeductionIds = this.openedDeductionIds.filter(id => id !== row.id)
        return
      }

      this.detailsLoadingId = row.id
      getEmployeesHeslbLoansDeductions(row.id)
        .then(response => {
          const deductions = Array.isArray(response.data) ? response.data : [response.data]
          this.$set(row, 'deductions', deductions)
          this.$refs.loanTable.toggleChildRow(row.id)
          this.openedDeductionIds.push(row.id)
          this.detailsLoadingId = null
        })
        .catch(() => {
          this.detailsLoadingId = null
        })
    },
    isDetailsOpen(id) {
      return this.openedDeductionIds.includes(id)
    },
    formatAmount(value) {
      const amount = Number(value || 0)
      const hasFraction = !Number.isInteger(amount)
      return amount.toLocaleString('en-US', {
        minimumFractionDigits: hasFraction ? 2 : 0,
        maximumFractionDigits: 2,
      })
    },
    formatDateTime(date) {
      if (!date) return '-'
      return format(parseISO(date), 'dd MMMM yyyy HH:mm')
    },
    formatPayrollMonth(date) {
      if (!date) return '-'
      return format(parseISO(date), 'MMM yyyy')
    },
    onLoanSearch() {
      this.searchLoan(this)
    },
    searchLoan: debounce(vm => {
      vm.pagination.page = 1
      vm.fetchItems()
    }, 350),
  }
}
</script>

<style lang="sass">
.heslb-loans__details-preloader
  width: 20px
  height: 20px

.heslb-loans__details-control
  width: 100px
  justify-content: center

.heslb-loans__details-arrow--open
  transform: rotate(180deg)
</style>
